import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { BiNetworkChart } from 'react-icons/bi';
import PageHeader from 'Utils/PageHeader';
import FetchContainer from 'Utils/FetchContainer';
import AppContext from 'Utils/AppContext';
import Breadcrumbs from 'Utils/Breadcrumbs';
import { useConfirm } from 'Utils/ConfirmDialog';
import { apiErrorText } from 'Utils/errorText';
import { READ_ONLY_TITLE } from 'Auth/permissions';
import { listDataGraphs, createDataGraph, deleteDataGraph, graphKeyLabel, graphHopsLabel, graphOutcome } from './dataGraphApi';
import { GRAPH_BASE, graphPath } from './paths';

// One data source's named data graphs. Each is what a TDM subset pulls from the source — the key
// rows are matched on, how far children are followed, and its own manual links and edge
// decisions. A subset source picks one of these; a source with none shows "Not configured" in the
// subset editor. Creating names a graph and opens it, where the rest is set.
const SourceGraphs = () => {
  const { dataSourceId } = useParams();
  const history = useHistory();
  const { connections, isReadOnly } = useContext(AppContext);
  const { confirm, confirmDialog } = useConfirm();
  const connection = useMemo(() => (connections || []).find((c) => String(c.id) === String(dataSourceId)), [connections, dataSourceId]);
  const [graphs, setGraphs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setGraphs(await listDataGraphs({ dataSourceId }));
    } catch (e) {
      setError(apiErrorText(e, 'Could not load the data graphs'));
    } finally {
      setLoading(false);
    }
  }, [dataSourceId]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true);
    setNotice(null);
    try {
      const g = await createDataGraph({ dataSourceId: Number(dataSourceId), name: name.trim() });
      history.push(graphPath(dataSourceId, g.id));
    } catch (e) {
      setNotice(apiErrorText(e, 'Could not create the data graph'));
      setBusy(false);
    }
  };

  const remove = async (g) => {
    const ok = await confirm({
      title: `Delete data graph "${g.name}"?`,
      body: 'Its manual links and edge decisions are deleted with it. A subset that uses it must pick another data graph first.',
      confirmLabel: 'Delete data graph',
      variant: 'danger',
    });
    if (!ok) return;
    setNotice(null);
    try {
      await deleteDataGraph(g.id);
      await load();
    } catch (e) {
      setNotice(apiErrorText(e, 'Could not delete the data graph'));
    }
  };

  const sourceName = (connection && connection.name) || (graphs[0] && graphs[0].dataSourceName) || `Source ${dataSourceId}`;

  return (
    <FetchContainer fetchingData={loading} absolute={true} error={error}>
      <div className='dg-page'>
        {confirmDialog}
        <div className='dg-breadcrumb'>
          <Breadcrumbs className='mb-0' items={[{ label: 'Data Graph', to: GRAPH_BASE }, { label: sourceName }]} />
        </div>
        <PageHeader
          icon={BiNetworkChart}
          eyebrow='Advanced Settings · Data Graph'
          header={sourceName}
          tooltip='The named data graphs of this source — what a test data subset pulls from it'
          help='Each data graph is one way to pull this source into a subset: the key its rows are matched on, everything connected to it or a list of objects with their rules, and its own manual links. A subset source picks one.'
          meta={<span>{graphs.length} data graph{graphs.length === 1 ? '' : 's'}</span>}
        />
        {notice && <div className='dg-error mb-3' data-testid='dg-graphs-error'>{notice}</div>}
        {graphs.length === 0 && (
          <div className='dg-empty mb-3' data-testid='dg-graphs-empty'>
            No data graph yet. Subsets cannot pull from this source until one exists: it shows as &quot;Not configured&quot; in the subset editor.
          </div>
        )}
        {graphs.length > 0 && (
          <div className='dg-table mb-3' role='table' aria-label='Data graphs'>
            <div className='dg-table__head dg-table__head--graphs' role='row'>
              <span>Name</span><span>Key</span><span className='dg-num'>Max hops</span><span>Used by</span><span />
            </div>
            {graphs.map((g) => (
              <div className='dg-table__row dg-table__row--graphs' role='row' key={g.id} data-testid={`dg-graph-${g.id}`}>
                <span className='dg-name'>
                  {g.name}
                  {graphOutcome(g) === 'LIST' && (
                    <span className='dg-badge' data-testid={`dg-graph-list-${g.id}`}
                      title='A list of objects, each reached by its rules'>List · {(g.objects || []).length} object{(g.objects || []).length === 1 ? '' : 's'}</span>
                  )}
                </span>
                <span className={graphKeyLabel(g) ? 'dg-mono dg-clip' : 'dg-muted'} title={graphKeyLabel(g) || undefined}>
                  {graphKeyLabel(g) || 'No key'}
                </span>
                <span className='dg-num'>{graphOutcome(g) === 'LIST' ? '—' : graphHopsLabel(g, connection)}</span>
                <span className='dg-muted dg-clip' title={(g.usedBy || []).join(', ') || undefined}>
                  {(g.usedBy || []).length ? g.usedBy.join(', ') : 'No subset'}
                </span>
                <span className='dg-actions'>
                  <Button size='sm' variant='outline-primary' data-testid={`dg-graph-open-${g.id}`}
                    onClick={() => history.push(graphPath(dataSourceId, g.id))}>Open</Button>
                  <Button size='sm' variant='link' data-testid={`dg-graph-delete-${g.id}`}
                    disabled={!!isReadOnly || (g.usedBy || []).length > 0}
                    title={isReadOnly ? READ_ONLY_TITLE : ((g.usedBy || []).length > 0 ? 'Used by a subset: pick another data graph there first' : undefined)}
                    onClick={() => remove(g)}>Delete</Button>
                </span>
              </div>
            ))}
          </div>
        )}
        {!isReadOnly && (
          <div className='dg-builder' data-testid='dg-graph-new'>
            <input className='dg-input dg-input--wide' placeholder='New data graph name, e.g. Members by person id' value={name}
              onChange={(e) => setName(e.target.value)} data-testid='dg-graph-new-name' aria-label='New data graph name' />
            <Button size='sm' disabled={busy || !name.trim()} onClick={create} data-testid='dg-graph-create'>
              New data graph
            </Button>
          </div>
        )}
      </div>
    </FetchContainer>
  );
};

export default SourceGraphs;
